import { type KeyboardEvent, useCallback, useState } from 'react';

import { TextInput } from 'cpool-components/input/TextInput';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import { useSearchLoanRequests } from '~/features/whatsapp/hooks/useSearchLoanRequests';

const MIN_QUERY_LENGTH = 2;

type Props = {
    excludeLoanRequestIds: Array<string>;
    onSelect: (loanRequestId: string) => void;
    isLinking: boolean;
};

export const LinkCaseSearch = ({ excludeLoanRequestIds, isLinking, onSelect }: Props) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const trimmedQuery = query.trim();
    const [debouncedQuery] = useDebounce(trimmedQuery, 300);
    const { data: results, isFetching } = useSearchLoanRequests(query);

    const options = (results ?? []).filter((result) => !excludeLoanRequestIds.includes(result.id));
    const showResults = trimmedQuery.length >= MIN_QUERY_LENGTH;
    const isSearching = trimmedQuery !== debouncedQuery || isFetching;

    const selectOption = useCallback(
        (loanRequestId: string) => {
            onSelect(loanRequestId);
            setQuery('');
        },
        [onSelect],
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || isLinking || isSearching || options.length === 0) return;
        event.preventDefault();
        selectOption(options[0].id);
    };

    return (
        <div className="flex flex-col gap-1">
            <TextInput
                id="link-case-search"
                placeholder={t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.SEARCH_PLACEHOLDER')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLinking}
            />
            {showResults && (
                <div
                    className="max-h-48 overflow-y-auto rounded-md border border-outline-light bg-white shadow-1"
                    role="listbox"
                >
                    {isSearching && (
                        <p className="px-3 py-2 text-sm text-grey">
                            {t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.SEARCH_LOADING')}
                        </p>
                    )}
                    {!isSearching && options.length === 0 && (
                        <p className="px-3 py-2 text-sm text-grey">
                            {t('WHATSAPP_CHAT.USER_INFO.LINKED_CASES.SEARCH_EMPTY')}
                        </p>
                    )}
                    {!isSearching &&
                        options.map((result) => (
                            <button
                                key={result.id}
                                type="button"
                                role="option"
                                disabled={isLinking}
                                onClick={() => selectOption(result.id)}
                                className="block w-full truncate px-3 py-2 text-left text-sm text-midnight hover:bg-surface-light disabled:opacity-50"
                            >
                                {result.antragsteller1
                                    ? `${result.antragsteller1.personendaten.vorname} ${result.antragsteller1.personendaten.nachname}`
                                    : result.id}
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};
